import { DtModel } from "./DtModelLMV";
import { DtConstants } from "./schema/DtConstants";
import { ElementFlags, QC, ColumnFamilies, RowKeySize, ModelIdSize } from "./schema/dt-schema";
import { DT_MODEL_CHANGED_EVENT, DT_TICKETS_CHANGED_EVENT } from "./DtEventTypes";
import { makeNewEncodedElementID, base64ConcatEnc, base64DecToArr, base64EncArr } from "./encoding/base64";
import { formatValueWithUnits } from "../lmv-compat/measurement/UnitFormatter";
import { PALETTE_TICKET_PRIORITY } from "./facets/Facets";

const WORKER_CREATE_ELEMENTS = "CREATE_ELEMENTS";
const WORKER_UPDATE_STREAMS_SETTINGS = "UPDATE_STREAMS_SETTINGS";

export class DefaultModel extends DtModel {
  constructor(facility, modelId) {
    super(facility, modelId);
    // All member initialization needs to go into #reset, to allow restoring the post-constructor state.
    this.#reset(true);
  }

  reset(modelData, loader) {
    super.reset(modelData, loader);
    this.#reset(false);
  }

  #reset(skipDispose = false) {
    if (!skipDispose) {
      this.#dispose();
    }

    this.ticketCache = null;
    this.boundOnModelChanged = this.onModelChanged.bind(this);
  }

  #dispose() {
    if (this.boundOnModelChanged) {
      this.facility.eventTarget.removeEventListener(DT_MODEL_CHANGED_EVENT, this.boundOnModelChanged);
      this.boundOnModelChanged = null;
    }

    if (this.ticketCache) {
      this.ticketCache.clear();
      this.ticketCache = null;
    }
  }

  dtor() {
    this.#dispose();
    super.dtor();
  }

  isDefault() {
    return true;
  }

  /**
   * Initializes the ticket cache and register event listeners for model change events
   */
  initTicketCache() {
    if (this.ticketCache) return;

    this.ticketCache = new Map();
    this.facility.eventTarget.addEventListener(
      DT_MODEL_CHANGED_EVENT,
      this.boundOnModelChanged
    );
  }

  /**
   * When tickets are modified, forces a refresh of the ticket cache.
   */
  onModelChanged(e) {
    if (e.model !== this) return;

    const { change } = e;
    const dbId2flags = this.getData()?.dbId2flags;
    if (!dbId2flags || !this.ticketCache) return;

    const changedDbIds = change.dbIds ?? [];
    const hasTicketsChanged = changedDbIds.some((dbId) =>
    (dbId2flags[dbId] & ElementFlags.Ticket) === ElementFlags.Ticket
    );
    const hasTicketsRemoved = change.dbIdsRemoved?.some((dbId) => this.ticketCache.has(dbId));

    // refresh tickets cache if anything changed
    if ((hasTicketsChanged || hasTicketsRemoved) && this.ticketCache) {
      this.getTickets(undefined, true);
    }
  }

  /**
   * Creates a new element in the model
   * Allows passing both Direct and Overwrite columns, QC.RowKey will be generated if not present
   * @param {object[]} elementsData element data
   * @param {string} desc context the update was made in
   * @param {string} [correlationId] - correlation id for the operation
   * @returns {Promise<string[]>} new element ids
   */
  async createElementsWithData(elementsData, desc, correlationId) {

    let muts = [];
    let keys = [];

    for (let i = 0; i < elementsData.length; i++) {
      const elmData = elementsData[i];

      let flags = elmData[QC.ElementFlags];

      if (typeof flags !== "number") {
        return Promise.reject("Element flags must be specified.");
      }

      let rowKey = elmData[QC.RowKey];

      if (rowKey && rowKey.length !== 32) {
        return Promise.reject("Element key must be 24 bytes (32 characters base64 encoded).");
      }

      if (!rowKey) {
        rowKey = makeNewEncodedElementID(flags & ElementFlags.AllLogicalMask);
      }

      for (let key in elmData) {
        if (key === QC.RowKey || key === QC.LmvDbId) {
          continue;
        }
        muts.push([key, elmData[key]]);
        keys.push(rowKey);
      }
    }

    const event = this.facility.app.waitForEvent(DT_MODEL_CHANGED_EVENT, {
      modelId: this.urn(),
      ctype: DtConstants.ChangeTypes.Mutate,
      isOwn: true
    });
    const req = this.asyncPropertyOperation({
      "operation": WORKER_CREATE_ELEMENTS,
      "muts": muts,
      "desc": desc,
      "rowIds": keys,
      "correlationId": correlationId
    });

    return Promise.all([req, event]).then(([reqRes, eventRes]) => reqRes);
  }

  /**
   *
   * @param {string} name - name of the ticket to create
   * @param {Array<[string,number]>} hosts Array of [modelUrn, dbId] tuples.
   * @returns Promise<{newIds: string[], newDbIds: number[]}>
   * @description Creates a new ticket element in the model.
   * The ticket will be created as a child of the given assets, if provided.
   * The assets should be a map of model urns to arrays of dbIds
   */
  async createTicket(name, hosts = []) {
    const data = {
      [QC.Name]: name,
      [QC.ElementFlags]: ElementFlags.Ticket,
      [QC.TandemCategory]: DtConstants.TC.Ticket
    };

    if (hosts.length) {
      await this.addParentXRef(data, hosts);
    }

    return this.createElementsWithData([data], DtConstants.ChangeDesc.CreateTicket);
  }

  /**
   * Ticket refers to Work Order or Issue in the context of a facility management system.
   * It is a "Ticket" element type stored in default model.
   * @typedef {Object} Ticket
   * @property {number} dbId - The dbId of the ticket
   * @property {string} externalId - The external ID of the ticket
   * @property {string} name - Name of the ticket
   * @property {string} level
   * @property {string} [tandemCategory]
   * @property {string} [classification]
   * @property {string} [priority] - Priority level of the ticket (Trivial/Low/Medium/High/Critical)
   * @property {string} [openDate] - Date string in YYYY-MM-DD format when the ticket was opened
   * @property {string} [closeDate] - Date string in YYYY-MM-DD format when the ticket was closed
   * @property {Object[]} [hosts] - Array of host element references this ticket is attached to
   * @property {Object} hosts[].model - The model containing the host element
   * @property {string|number} hosts[].hostId - The dbId of the host element
   * @property {Object} assetProps - Custom properties
   */

  /**
   * Returns a list of tickets in the facility.
   * @param {number[]} [dbIds] - Optional array of specific ticket dbIds to fetch. If not provided, fetches all tickets.
   * @param {boolean} [clearCache=false] - If true, clears ticket cache
   * @returns {Promise<Ticket[]>} Array of tickets
   */
  async getTickets(dbIds, clearCache = false) {
    this.initTicketCache();

    await this.waitForLoad(false, true);
    dbIds ??= this.getElementIds(ElementFlags.Ticket);
    if (!dbIds.length) {
      if (this.ticketCache.size > 0) {
        // All tickets were deleted
        this.ticketCache.clear();
        this.facility.eventTarget.dispatchEvent({ type: DT_TICKETS_CHANGED_EVENT, dbIds: [] });
      }
      return [];
    }

    if (clearCache) {
      this.ticketCache.clear();
    }

    const dbIdsToFetch = [];
    for (const dbId of dbIds) {
      if (!this.ticketCache.has(dbId)) {
        dbIdsToFetch.push(dbId);
      }
    }

    if (dbIdsToFetch.length > 0) {
      const bulkFetchPromise = this._doGetTickets(dbIdsToFetch).then((ticketsMap) => {
        this.facility.eventTarget.dispatchEvent({
          type: DT_TICKETS_CHANGED_EVENT,
          dbIds: dbIdsToFetch
        });


        return ticketsMap;
      });

      for (const dbId of dbIdsToFetch) {
        const individualTicketPromise = bulkFetchPromise.then((ticketsMap) => ticketsMap.get(dbId));
        this.ticketCache.set(dbId, individualTicketPromise);
      }
    }

    const allPromises = dbIds.map((dbId) => this.ticketCache.get(dbId));

    return Promise.all(allPromises);
  }

  async _doGetTickets(dbIds) {
    if (!dbIds.length) return new Map();

    const { dbId2xparentId, xrefId2fullId } = this.getData();
    const parentXrefsByTicket = dbIds.map((dbId) => {
      const xParentId = dbId2xparentId[dbId];

      if (!xParentId) {
        return [];
      }

      if (Array.isArray(xParentId)) {
        return xParentId.map((x) => xrefId2fullId[x]);
      }

      return [xrefId2fullId[xParentId]];
    });

    const [externalIds, resolvedParentsFlat, props] = await Promise.all([
    this.getElementIdsFromDbIds(dbIds),
    this.facility.resolveXrefs(parentXrefsByTicket.flat()),
    this.query({ dbIds, includes: { standard: true, applied: true } })]
    );


    const ticketsMap = new Map();
    let offset = 0;
    for (let idx = 0; idx < dbIds.length; idx++) {
      const dbId = dbIds[idx];
      const row = props.rows[idx];

      const hostCount = parentXrefsByTicket[idx].length;
      const resolvedHosts = resolvedParentsFlat.slice(offset, offset + hostCount);
      offset += hostCount;
      const name = row[QC.Name];

      if (parentXrefsByTicket[idx].length && resolvedHosts.some((h) => !h)) {
        console.warn(`Failed to resolve parent xref for ticket "${name}" (dbId: ${dbId}). Host element may have been deleted.`);
        // TODO: Handle case when host element is deleted
      }

      const assetProps = {};
      for (let key in row) {
        if (key.startsWith(ColumnFamilies.DtProperties)) {
          const attr = props.cols.find((col) => col.id === key);
          if (attr) {
            let value = '';
            if (row[key] != null) {
              const forgeUnit = attr.forgeUnit ? "autodesk.unit.unit:" + attr.forgeUnit + "-1.0.0" : attr.dataTypeContext;
              value = formatValueWithUnits(row[key], forgeUnit, attr.dataType, attr.precision, { symbolKey: attr.forgeUnit !== "dateTime" ? attr.forgeSymbol : '' });
            }
            assetProps[key] = { value, name: attr.displayName, hash: attr.hash };
          }
        }
      }

      const ticket = {
        dbId,
        name,
        level: row[QC.Level],
        tandemCategory: row[QC.TandemCategory],
        classification: row[QC.Classification],
        priority: row[QC.Priority],
        openDate: row[QC.OpenDate],
        closeDate: row[QC.CloseDate],
        externalId: externalIds[idx],
        hosts: resolvedHosts.filter(Boolean).map((host) => ({ model: host.model, hostId: host.dbId })),
        assetProps,
        priorityColor: PALETTE_TICKET_PRIORITY[row[QC.Priority]]
      };

      ticketsMap.set(dbId, ticket);
    }

    return ticketsMap;
  }

  /**
   * Prepare element data with host references (QC.XParent) and spatial information from hosts
   * @param {Object} elementData The element data object to update
   * @param {Array<[string,number]>} hosts Array of [urn, dbId] tuples for the hosts
   */
  async addParentXRef(elementData, hosts) {
    if (!hosts || hosts.length === 0) {
      // Clear hosts
      elementData[QC.XParent] = "";
      return;
    }

    const dbIdsByUrn = {};
    hosts.forEach(([urn, dbId]) => {
      dbIdsByUrn[urn] ??= [];
      dbIdsByUrn[urn].push(dbId);
    });

    const encoded = await this.facility.encodeIds(dbIdsByUrn, true);
    const encodedIds = hosts.map(([urn, id]) => {
      const enc = encoded[urn]?.[id];
      if (!enc) {
        console.warn(`addParentXRef: failed to encode host (${urn}, ${id})`);
      }
      return enc;
    }).filter(Boolean);

    if (encodedIds.length === 1) {
      elementData[QC.XParent] = encodedIds[0];
    } else if (encodedIds.length > 1) {
      elementData[QC.XParent] = base64ConcatEnc(encodedIds);
    } else {
      elementData[QC.XParent] = "";
    }

    // Copy spatial information from hosts
    await this._copyRoomsFromHosts(elementData, hosts);
    await this._copyLevelFromHosts(elementData, hosts);
  }

  /**
   * Assign host element references (QC.XParent) and copy spatial information from hosts
   *
   * @param {number[]} dbIds Array of dbIds of the elements whose hosts should be updated.
   * @param {Array<Array<[string,number]>>} hostsPerElement Array of tuples of hosts [urn, dbId] for each element. Empty array will "clear" the hosts.
   */
  async updateHosts(dbIds, hostsPerElement) {
    if (dbIds.length !== hostsPerElement.length) {
      console.error("dbIds and hostsPerElement length mismatch");
      return;
    }

    if (dbIds.length < 1) return;

    const changeData = [];
    for (let i = 0; i < dbIds.length; i++) {
      const dbId = dbIds[i];
      const hostList = hostsPerElement[i] || [];

      const elementData = {
        [QC.LmvDbId]: dbId
      };

      await this.addParentXRef(elementData, hostList);
      changeData.push(elementData);
    }

    return this.updateElementsWithData(changeData, DtConstants.ChangeDesc.UpdateHosts, undefined, true);
  }

  async updateStreamsSettings(dbIds, settings, desc) {
    return this.asyncPropertyOperation({
      operation: WORKER_UPDATE_STREAMS_SETTINGS,
      dbIds,
      settings,
      desc
    });
  }

  /**
   * Copy union of rooms from multiple hosts
   * @param {Object} elementData The element data object to update
   * @param {Array<[string,number]>} hosts Array of [urn, dbId] tuples for the hosts
   */
  async _copyRoomsFromHosts(elementData, hosts) {
    const allRoomXrefs = new Map();

    // collect all rooms from the host(s)
    for (const [urn, dbId] of hosts) {
      const model = this.facility.getModelByUrn(urn);
      if (!model) continue;

      const pkg = model.getData();
      const roomIds = pkg.dbId2roomIds[dbId];
      const xroomIds = pkg.dbId2xroomIds[dbId];
      const originalRoomCount = allRoomXrefs.size;

      if (Array.isArray(xroomIds)) {
        xroomIds.forEach((xrefId) => {
          const fullId = pkg.xrefId2fullId[xrefId];
          if (fullId) allRoomXrefs.set(fullId[0] + fullId[1], fullId);
        });
      } else if (xroomIds) {
        const fullId = pkg.xrefId2fullId[xroomIds];
        if (fullId) allRoomXrefs.set(fullId[0] + fullId[1], fullId);
      }

      if (roomIds) {
        const refIds = await model.getElementIdsFromDbIds(Array.isArray(roomIds) ? roomIds : [roomIds]);
        refIds.forEach((id) => allRoomXrefs.set(model.urn() + id, [model.urn(), id]));
      }

      // host element itself is a room
      if (originalRoomCount === allRoomXrefs.size && model.isRoom(dbId)) {
        const refIds = await model.getElementIdsFromDbIds([dbId]);
        refIds.forEach((id) => allRoomXrefs.set(model.urn() + id, [model.urn(), id]));
      }
    }

    if (allRoomXrefs.size === 0) return;

    const xrefs = Array.from(allRoomXrefs.values());
    const buf = new Uint8Array(xrefs.length * RowKeySize);

    xrefs.forEach(([urn, id], idx) => {
      const modelGuid = urn.slice(DtConstants.DT_MODEL_URN_PREFIX.length + 1);
      base64DecToArr(modelGuid, buf, idx * RowKeySize);
      base64DecToArr(id, buf, idx * RowKeySize + ModelIdSize);
    });

    elementData[QC.XRooms] = base64EncArr(buf);
  }

  /**
   * Copy levels from multiple hosts (only if all hosts share the same level)
   * @param {Object} elementData The element data object to update
   * @param {Array<[string,number]>} hosts Array of [urn, dbId] tuples for the hosts
   */
  async _copyLevelFromHosts(elementData, hosts) {
    if (hosts.length === 0) return;

    let sharedLevel = null;
    let sharedLevelName = null;

    // Check if all hosts have the same level and don't copy level if hosts differ in level
    for (const [urn, dbId] of hosts) {
      const model = this.facility.getModelByUrn(urn);
      if (!model) continue;

      const levelId = model.getData().dbId2levelId[dbId];
      if (levelId <= 0) continue;

      const modelLevels = model.getLevels();
      const level = modelLevels[levelId];
      if (!level) continue;

      if (sharedLevel === null) {
        sharedLevel = level;
        sharedLevelName = level.name;
      } else if (level.name !== sharedLevelName) {
        return;
      }
    }

    if (sharedLevel) {
      await this.facility.addLevelFromElement(elementData, this.facility.getModelByUrn(hosts[0][0]), sharedLevel.dbId);
    }
  }

  async refreshStreamConfigsIfNeeded(dbIds, muts) {
    // if the change affects stream classification, refresh the stream configs
    const hasStreams = dbIds.some((dbId) => {
      const flags = this.getData().dbId2flags[dbId];
      return flags === ElementFlags.Stream;
    });

    if (!hasStreams) {
      return;
    }

    // indicates that a number of streams on a connection might change as a result of this action - trigger a refresh of the stream configs
    const isAffectingStreamConfigs = muts.some((mut) => {
      const qcol = mut[0];
      return qcol === QC.Classification || qcol === QC.OClassification ||
      qcol === QC.UniformatClass || qcol === QC.OUniformatClass ||
      qcol === QC.TandemCategory || qcol === QC.OTandemCategory;
    });

    if (isAffectingStreamConfigs) {
      await this.facility.getStreamManager().refreshStreamConfigs(DtConstants.ChangeDesc.RefreshStreamConfigsClassificationChange);
    }
  }
}